import { Dispatch, SetStateAction, useRef, useState } from "react";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonRadio,
  IonRadioGroup,
  IonText,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RequestModel, SchoolModel, SubModel } from "model";
import { useSubProfile } from "pages/SubProfile/hooks";
// FIXME: Change this to use `Icon` from `swing-components`
import { MdArrowBack, MdArrowForward, MdChevronRight } from "react-icons/md";
import { subPhotoPlaceholderPNG } from "swing-assets";
import { Alert } from "swing-components";
import { generateSwingSupportURL } from "swing-utils";

import { SubProfile } from "~components/swing-react-components";
import {
  COMBINED_RESTRICTIONS,
  confirmWaitlistedSub,
  translate,
  useShouldRestrictAdmin,
} from "~utils";

import { SubInfoCard } from "../SubInfoCard/SubInfoCard";
import styles from "./SubWaitlistSelectionView.module.css";

type SubWaitlistSelectionViewProps = {
  request: RequestModel;
};

export function SubWaitlistSelectionView({ request }: SubWaitlistSelectionViewProps) {
  const hasChooseSubRestriction = useShouldRestrictAdmin([COMBINED_RESTRICTIONS.CHOOSE_SUB]);

  if (hasChooseSubRestriction) {
    return <></>;
  }

  return (
    <SubInfoCard
      status="WAITLIST"
      waitlistSubs={request.pool.map((sub) => ({ profileImageUrl: sub.photo }))}
      isChooseSub
    >
      <div css={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 16 }}>
        <IonText>
          <span>{request.pool.length}</span>{" "}
          {request.pool.length > 1
            ? translate("WAITLIST_SUB_COUNT_LABEL_MULTIPLE")
            : translate("WAITLIST_SUB_COUNT_LABEL_SINGLE")}
        </IonText>
        <div>
          <img
            css={{ width: 92, height: 92, borderRadius: "50%" }}
            src={subPhotoPlaceholderPNG}
            alt="user default icon"
          />
        </div>
        <IonButton id="open-sub-selection-modal" css={{ width: 140 }}>
          {translate("CHOOSE_SUB_LABEL")}
        </IonButton>
        <SubSelectionModal
          trigger="open-sub-selection-modal"
          requestId={request.id}
          pool={request.pool}
          school={request.school}
        />
      </div>
    </SubInfoCard>
  );
}

type SubSelectionModalProps = {
  trigger: string;
  requestId: string;
  pool: SubModel[];
  school: SchoolModel;
};

function SubSelectionModal(props: SubSelectionModalProps) {
  const { trigger, requestId, pool, school } = props;

  const modal = useRef<HTMLIonModalElement>(null);
  const dismissModal = () => modal?.current?.dismiss();
  const [selectedSubId, setSelectedSubId] = useState<string>("");
  const [formState, setFormState] = useState<"subSelection" | "confirm" | "subDetails">(
    "subSelection",
  );
  const [hasApiError, setHasApiError] = useState(false);

  const queryClient = useQueryClient();

  const selectedSub = pool.find((item) => item.id === selectedSubId);

  const subName = selectedSub?.name || "";
  const hasConflict = selectedSub?.hasConflict || false;

  const refreshForm = () => {
    setSelectedSubId("");
    setFormState("subSelection");
  };

  const confirmModal = () => {
    setFormState("confirm");
  };

  const goToSubDetailsStep = () => {
    setFormState("subDetails");
  };

  const { mutate: postConfirmation } = useMutation({
    mutationFn: () => {
      return confirmWaitlistedSub(requestId, selectedSubId);
    },
    onSuccess: () => {
      dismissModal();
      queryClient.invalidateQueries();
    },
    onError: (err) => {
      setHasApiError(true);
      // eslint-disable-next-line no-console
      console.error(err);
    },
  });

  const confirmSub = () => {
    postConfirmation();
  };

  return (
    <IonModal trigger={trigger} ref={modal} onDidDismiss={refreshForm}>
      {formState === "subSelection" && (
        <SubSelectionFormStage
          selectedSubId={selectedSubId}
          onSubChange={setSelectedSubId}
          model={pool}
          school={school}
          dismissModal={dismissModal}
          confirmModal={confirmModal}
          goToSubDetailsStep={goToSubDetailsStep}
        />
      )}
      {formState === "confirm" && (
        <SubConfirmFormStage
          hasApiError={hasApiError}
          onSetHasApiError={(value) => setHasApiError(value)}
          subName={subName}
          subHasConflict={hasConflict}
          dismissModal={refreshForm}
          onConfirm={confirmSub}
        />
      )}
      {formState === "subDetails" && (
        <SubInfoModal dismiss={refreshForm} subId={selectedSubId} confirmModal={confirmModal} />
      )}
    </IonModal>
  );
}

type SubInfoModalProps = {
  dismiss: () => void;
  confirmModal: () => void;
  subId?: string;
};

function SubInfoModal(props: SubInfoModalProps) {
  const { dismiss, subId, confirmModal } = props;

  /*** Query ***/
  const subProfile = useSubProfile(subId || "");

  /*** Render ***/
  // TODO: Should we add a loader?
  if (!subId) return <></>;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={dismiss}>
              <MdArrowBack className="swing-icon-tooltip icon-2xl" />
            </IonButton>
          </IonButtons>
          <IonTitle>{translate("SUB_PROFILE_LABEL")}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <SubProfile {...subProfile} isSwingHero={subProfile?.stats?.isSwingHero} />
        <div className={styles.bottomHoverSection}>
          <IonButton
            expand="block"
            onClick={() => {
              confirmModal();
            }}
          >
            {translate("SELECT_THIS_SUB_LABEL")}
          </IonButton>
        </div>
      </IonContent>
    </IonPage>
  );
}

type SubSelectionFormStageProps = {
  selectedSubId: string;
  onSubChange: Dispatch<SetStateAction<string>>;
  model: SubModel[];
  school: SchoolModel;
  dismissModal: React.MouseEventHandler<HTMLIonButtonElement>;
  confirmModal: () => void;
  goToSubDetailsStep: () => void;
};

const SubSelectionFormStage: React.FC<SubSelectionFormStageProps> = ({
  selectedSubId,
  onSubChange,
  model,
  school,
  dismissModal,
  confirmModal,
  goToSubDetailsStep,
}) => {
  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{translate("CHOOSE_SUB_LABEL")}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={dismissModal}>
              <IonText color="primary">{translate("LABEL_CLOSE")}</IonText>
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonList className={`${styles.listFill}`}>
          <IonItem lines="none">
            {translate("CHOOSE_SUB_FOR_LABEL")} {school.name}
          </IonItem>
          <IonRadioGroup
            value={selectedSubId}
            onIonChange={(e) => {
              onSubChange(e.target.value);
            }}
          >
            {model.map((sub) => (
              <IonItem lines="inset" key={sub.id}>
                <IonRadio slot="start" value={sub.id} />
                <IonLabel>
                  <div className="flex items-center">
                    <div
                      className="flex-1 overflow-ellipsis"
                      onClick={() => {
                        onSubChange(sub.id);
                      }}
                    >
                      {sub.name}
                    </div>
                    <IonButton
                      className={styles.visitProfileButton}
                      fill="clear"
                      onClick={() => {
                        onSubChange(sub.id);
                        goToSubDetailsStep();
                      }}
                    >
                      <span>{translate("PROFILE_LABEL")}</span>
                      <MdChevronRight className="icon-2xl" />
                    </IonButton>
                  </div>
                </IonLabel>
              </IonItem>
            ))}
          </IonRadioGroup>
          <div className={styles.submitButtonHolder}>
            <IonButton
              id="subSelectionState"
              disabled={selectedSubId.length < 1}
              className="w-full ion-no-margin"
              onClick={confirmModal}
            >
              {translate("LABEL_NEXT")} <MdArrowForward />
            </IonButton>
          </div>
        </IonList>
      </IonContent>
    </IonPage>
  );
};

type SubConfirmFormStageProps = {
  subName: string;
  subHasConflict: boolean;
  hasApiError: boolean;
  onSetHasApiError: (hasError: boolean) => void;
  dismissModal: () => void;
  onConfirm: () => void;
};

const SubConfirmFormStage: React.FC<SubConfirmFormStageProps> = ({
  subName,
  subHasConflict,
  hasApiError,
  onSetHasApiError,
  dismissModal,
  onConfirm,
}) => {
  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{translate("CONFIRM_SUB_HEADER")}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => dismissModal()}>
              <IonText color="primary">{translate("LABEL_CLOSE")}</IonText>
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      {hasApiError && (
        <div className="ion-margin-top">
          <Alert
            color="danger"
            onDismiss={() => onSetHasApiError(false)}
            showIcon
            title={translate("LABEL_SOMETHING_WENT_WRONG")}
            className="mx-4"
          >
            <span>
              {translate("LABEL_CONTACT_SUPPORT_1")}{" "}
              <a
                target="_blank"
                rel="noreferrer"
                href={generateSwingSupportURL("/hc/en-us/requests/new")}
              >
                {translate("LABEL_CONTACT_SUPPORT")}
              </a>{" "}
              {translate("LABEL_CONTACT_SUPPORT_2")}
            </span>
          </Alert>
        </div>
      )}
      <IonContent>
        <IonList className={`${styles.listFill}`} lines="none">
          <IonItem>
            <div>
              <p>{translate("PLEASE_CONFIRM_LABEL")}:</p>
              <p>
                {translate("SELECTING_SUB_LABEL_1")} <span className="font-medium">{subName}</span>{" "}
                {translate("SELECTING_SUB_LABEL_2")}.
              </p>
              {subHasConflict && (
                <p>
                  {translate("COMMITMENTS_LABEL", subName)}
                  <br />
                  <br />
                  {`
                  ${translate("COMMITMENTS_EMAIL_LABEL")}
                  `}
                </p>
              )}
            </div>
          </IonItem>
          <div className={styles.submitButtonHolder}>
            <IonButton className="ion-no-margin w-full" onClick={() => onConfirm()}>
              {translate("LABEL_CONFIRM")}
            </IonButton>
            <IonButton className="w-full ion-no-margin" fill="outline" onClick={dismissModal}>
              {translate("LABEL_NEVERMIND")}
            </IonButton>
          </div>
        </IonList>
      </IonContent>
    </IonPage>
  );
};
